import { IBusinessEntities, IBusinessEntity } from '@commenda/types';
import { fetcher } from '@commenda/utils';
import { useQuery } from '@tanstack/react-query';
import { useApiScope } from '../scope';
import { businessEntityKeys } from './keys';

interface UseBusinessEntitiesOfCompanyOptions {
  includeNonControlled?: boolean;
}

const getBusinessEntitiesOfCompany = async (
  companyId: number,
  options: UseBusinessEntitiesOfCompanyOptions = {},
  shareToken: string | undefined,
) => {
  const readFrom = async (cursor: number | null): Promise<IBusinessEntity[]> => {
    const query = new URLSearchParams({ companyId: String(companyId), limit: '100' });
    if (options.includeNonControlled) query.append('includeNonControlled', 'true');
    if (cursor !== null) query.append('cursor', String(cursor));

    const response = await fetcher(`business-entities?${query}`, { method: 'GET', shareToken });
    if (!response.ok) throw new Error('Failed to fetch business entity');

    const page = (await response.json()) as { entities: IBusinessEntity[]; nextCursor: number | null };
    return page.nextCursor === null ? page.entities : [...page.entities, ...(await readFrom(page.nextCursor))];
  };

  return { entities: await readFrom(null) } satisfies IBusinessEntities;
};

const useBusinessEntitiesOfCompany = (
  companyId: number,
  options: UseBusinessEntitiesOfCompanyOptions = {},
) => {
  const { shareToken } = useApiScope();

  return useQuery({
    queryFn: () => getBusinessEntitiesOfCompany(companyId, options, shareToken),
    queryKey: [
      businessEntityKeys.FETCH_BUSINESS_ENTITIES_OF_COMPANY,
      companyId,
      !!options.includeNonControlled,
      shareToken,
    ],
    staleTime: 0,
    refetchOnMount: true,
  });
};

export { useBusinessEntitiesOfCompany };
